import { useState, useEffect, useRef, type ReactElement } from 'react';
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import BusinessIcon from '@mui/icons-material/Business';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BuildIcon from '@mui/icons-material/Build';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@/core/api';
import type { ResponseDashboardDTO } from '@/core/api';
import { fromAxiosError } from '@/core/api';
import { daysAgoIso, todayIso } from '@/core/utils';
import { BackToTopFab, HelpAlert, usePageTitle, usePersistedState, useDocumentVisibility } from '@/shared';

import PendingApplications from './PendingApplications';
import type { StatusTone } from '@/core/utils/statusTone';
import ActivityBarChart from './ActivityBarChart';
import KpiCard from './KpiCard';
import KpiCardSkeleton from './KpiCardSkeleton';
import LiveTowRequests from './LiveTowRequests';

const LS_KEY = 'itow.preferences.homeAutoRefreshSec';

/**
 * Icon + semantic tint per KPI, keyed by the DTO field name.
 *
 * The mockups tint each KPI's icon tile by what the number MEANS, not by
 * section — so a row of tiles reads at a glance: red needs attention, amber is
 * pending, green is settled, blue/navy is informational volume.
 */
/**
 * `.eyebrow` from the mockups — a small tracked uppercase label that groups the
 * KPI rows without competing with the page title for weight.
 */
const sectionEyebrowSx = {
  fontSize: '0.6875rem',
  fontWeight: 700,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: 1.25,
} as const;

const KPI_VISUALS: Record<string, { icon: ReactElement; tone: StatusTone }> = {
  // Last 24 hours
  unreadMessage:                { icon: <ChatBubbleOutlineIcon />,  tone: 'info' },
  newClaims:                    { icon: <DescriptionOutlinedIcon />, tone: 'primary' },
  closedClaims:                 { icon: <CheckCircleOutlineIcon />, tone: 'success' },
  reopenClaims:                 { icon: <ReplayIcon />,             tone: 'warning' },
  // A tow request with no provider attached is the one that needs a human.
  towRequestWhitoutTowProvider: { icon: <ErrorOutlineIcon />,       tone: 'error' },
  // Last week
  openClaims:                   { icon: <FolderOpenIcon />,         tone: 'info' },
  newAdjusters:                 { icon: <ManageAccountsIcon />,     tone: 'primary' },
  newCustomers:                 { icon: <BusinessIcon />,           tone: 'primary' },
  newDrivers:                   { icon: <DriveEtaIcon />,           tone: 'primary' },
  towRequest:                   { icon: <LocalShippingIcon />,      tone: 'info' },
  roadSideAssintence:           { icon: <BuildIcon />,              tone: 'warning' },
};

// Valid interval options. `null` means auto-refresh is disabled.
const REFRESH_OPTIONS: Array<{ value: number | null; labelKey: string }> = [
  { value: 30,   labelKey: 'home.autoRefresh30s' },
  { value: 60,   labelKey: 'home.autoRefresh60s' },
  { value: 120,  labelKey: 'home.autoRefresh2m'  },
  { value: 300,  labelKey: 'home.autoRefresh5m'  },
  { value: null, labelKey: 'home.autoRefreshOff' },
];


export default function AdministratorHome() {
  const { t, i18n } = useTranslation();
  usePageTitle('home.administrator.pageTitle');
  const navigate = useNavigate();

  const [claimNumber, setClaimNumber] = useState('');
  const [data, setData] = useState<ResponseDashboardDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const [autoRefreshSec, setAutoRefreshSec] = usePersistedState<number | null>(
    LS_KEY,
    60,
    (v) => String(v),
    (s) => {
      if (s === 'null') return null;
      const n = Number(s);
      // Guard: only accept values that exist in REFRESH_OPTIONS to avoid stale arbitrary numbers.
      return REFRESH_OPTIONS.some((o) => o.value === n) ? n : 60;
    },
  );

  // Keep a ref so the interval effect can read the latest version bumper without
  // re-subscribing; the interval itself only depends on autoRefreshSec.
  const bumpVersion = useRef(() => setVersion((v) => v + 1));
  useEffect(() => {
    bumpVersion.current = () => setVersion((v) => v + 1);
  });

  // Pause auto-refresh when the tab is hidden — saves API calls + battery on
  // background tabs. Resumes immediately on `visibilitychange` back to visible
  // (the next interval tick fires on schedule).
  const isVisible = useDocumentVisibility();
  useEffect(() => {
    if (autoRefreshSec === null || !isVisible) return;
    const id = setInterval(() => bumpVersion.current(), autoRefreshSec * 1000);
    return () => clearInterval(id);
  }, [autoRefreshSec, isVisible]);

  function handleAutoRefreshChange(e: SelectChangeEvent<string>) {
    const raw = e.target.value;
    const next: number | null = raw === 'null' ? null : Number(raw);
    setAutoRefreshSec(next);
  }

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setErrorMessage(null);

    // Matches Angular's fnDate.GetValueTimeZoneOffSet(): hours from UTC,
    // sign-flipped so west-of-UTC (e.g. US Central = UTC-6) gives -6.
    const timeOffSet = (new Date().getTimezoneOffset() / 60) * -1;

    async function fetchDashboard() {
      try {
        const result = await dashboardApi.getAdminDashboard(timeOffSet);
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(fromAxiosError(err).message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchDashboard();

    return () => {
      cancelled = true;
    };
  }, [version]);

  if (errorMessage !== null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <strong>{t('home.errorTitle')}: </strong>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  const ns = 'HOME.DASHBOARD.ADMIN';

  // Only compute chart data when the DTO is available.
  const dayLabelFormatter = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
  const toSeries = (entries: typeof data extends null ? never : { date: string; count: number }[]) =>
    entries.map((entry) => ({
      label: dayLabelFormatter.format(new Date(entry.date)),
      count: entry.count,
    }));

  const driverActivity = data ? toSeries(data.driverActivity) : [];
  // Tolerated as absent so a client running against an older API renders the
  // tow chart rather than blowing up on a field that is not there yet.
  const roadsideActivity = data ? toSeries(data.roadsideActivity ?? []) : [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header — sticky so title and refresh controls stay accessible on scroll */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'background.default',
          zIndex: 1,
          py: 1,
          mb: 2,
        }}
      >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h1" sx={{ color: 'primary.main' }}>
          {t('home.administrator.pageTitle')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, '@media print': { display: 'none' } }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel id="auto-refresh-label">{t('home.autoRefresh')}</InputLabel>
            <Select<string>
              labelId="auto-refresh-label"
              label={t('home.autoRefresh')}
              value={String(autoRefreshSec)}
              onChange={handleAutoRefreshChange}
            >
              {REFRESH_OPTIONS.map((opt) => (
                <MenuItem key={String(opt.value)} value={String(opt.value)}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title={t('actions.refresh')}>
            <span>
              <IconButton
                aria-label={t('actions.refresh')}
                size="small"
                onClick={() => setVersion((v) => v + 1)}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      </Box>

      {/* Above the welcome help, because somebody is waiting on it. */}
      <PendingApplications />

      <HelpAlert
        persistKey="home.administrator.welcome"
        severity="info"
        variant="outlined"
        title={t('HELP.home.administrator.welcome.title')}
      >
        {t('HELP.home.administrator.welcome.body')}
      </HelpAlert>

      {/* ------------------------------------------------------------------ */}
      {/* Section 1 — Last 24 Hours (5 KPI cards)                            */}
      {/* ------------------------------------------------------------------ */}
      <Typography component="h2" sx={sectionEyebrowSx}>
        {t(`${ns}.last24Hours.title`)}
      </Typography>
      {/* towRequestWhitoutTowProvider drills to /tow-requests with a 1-day  */}
      {/* startDate (Angular admin 24h section, home.component.ts line ~177). */}
      {/* Other 24h cards target claims/chat pages not yet built.            */}
      <Grid container spacing={2} sx={{ mb: 4 }} aria-busy={loading || !data}>
        {loading || !data
          ? Array.from({ length: 5 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={i}>
                <KpiCardSkeleton />
              </Grid>
            ))
          : (
            // These three used to share one target, `/claims?status=OPEN`: a
            // "New Claims 12" tile landed on a list of 27, and Reopen Claim and
            // Open Claims landed on the same 27. Status alone can't express
            // "in the last 24 hours" — but `isDashboard` can. Under that flag
            // ClaimsBLL swaps the date column for the one the KPI counted:
            // closeDate for CLOSE, reOpenDate when isReopen is set, creationDate
            // otherwise (ClaimsBLL.cs:609-641). ClaimsList now forwards both.
            [
              // This tile has now been wrong twice and is finally right.
              //
              // It first pointed at `/catalogs/administrator-chat` when that
              // was a catalog stub listing *every* chat group in the system,
              // which neither matched the count (this counts only messages
              // addressed to the signed-in admin) nor could show a message —
              // there was no REST endpoint returning chat history at all.
              // It was then made non-clickable, which was honest but left the
              // messages unreadable.
              //
              // That endpoint now exists, and the same path renders a real
              // conversation view scoped to the signed-in user. The tooltip
              // stays: "what does this number count" was the actual question
              // behind the original report.
              {
                key: 'unreadMessage',
                value: data.hours.unreadMessage,
                linkTo: '/catalogs/administrator-chat',
                helpText: t(`${ns}.last24Hours.unreadMessageHelp`),
              },
              {
                key: 'newClaims',
                value: data.hours.newClaims,
                // creationDate >= 24h ago, **any status** — `hours.newClaims`
                // applies no status predicate (DashboardBLL.cs:50). Passing
                // status=OPEN here narrowed the list to 4 against a tile of 12;
                // without it the dashboard branch falls through to its default
                // creationDate window and the two agree.
                linkTo: '/claims?' + new URLSearchParams({ startDate: daysAgoIso(1), isDashboard: 'true' }).toString(),
              },
              {
                key: 'closedClaims',
                value: data.hours.closedClaims,
                // status CLOSE switches the window onto closeDate
                linkTo: '/claims?' + new URLSearchParams({ status: 'CLOSE', startDate: daysAgoIso(1), isDashboard: 'true' }).toString(),
              },
              {
                key: 'reopenClaims',
                value: data.hours.reopenClaims,
                // isReopen switches the window onto reOpenDate; the backend
                // aliases status REOPEN → OPEN, which is the state they land in.
                linkTo: '/claims?' + new URLSearchParams({ status: 'REOPEN', startDate: daysAgoIso(1), isDashboard: 'true', isReopen: 'true' }).toString(),
              },
              {
                key: 'towRequestWhitoutTowProvider',
                value: data.hours.towRequestWhitoutTowProvider,
                linkTo: '/tow-requests?' + new URLSearchParams({ startDate: daysAgoIso(1), isDashboard: 'true' }).toString(),
              },
            ] satisfies Array<{ key: string; value: number; linkTo: string | undefined; helpText?: string }>
          ).map(({ key, value, linkTo, helpText }) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={key}>
              <KpiCard
                label={t(`${ns}.last24Hours.${key}`)}
                value={value}
                icon={KPI_VISUALS[key]?.icon}
                tone={KPI_VISUALS[key]?.tone}
                linkTo={linkTo}
                helpText={helpText}
              />
            </Grid>
          ))
        }
      </Grid>

      {/* ------------------------------------------------------------------ */}
      {/* Section 2 — Last Week (7 KPI cards)                                */}
      {/* ------------------------------------------------------------------ */}
      <Typography component="h2" sx={sectionEyebrowSx}>
        {t(`${ns}.lastWeek.title`)}
      </Typography>
      {/* towRequest and towRequestWhitoutTowProvider drill to /tow-requests   */}
      {/* with status=OPEN and a 7-day date window (Angular admin week section, */}
      {/* home.component.ts lines ~257 and ~284). Other week cards target       */}
      {/* claims/users pages not yet built.                                     */}
      <Grid container spacing={2} aria-busy={loading || !data}>
        {loading || !data
          ? Array.from({ length: 7 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <KpiCardSkeleton />
              </Grid>
            ))
          : (
            [
              {
                key: 'openClaims',
                value: data.week.openClaims,
                // claimDate inside the 7-day window — which is what the plain
                // (non-dashboard) /claims date filter matches on, so no flag here.
                linkTo: '/claims?' + new URLSearchParams({ status: 'OPEN', startDate: daysAgoIso(7), endDate: todayIso() }).toString(),
              },
              {
                key: 'newAdjusters',
                value: data.week.newAdjusters,
                linkTo: '/catalogs/adjusters',
              },
              {
                key: 'newCustomers',
                value: data.week.newCustomers,
                linkTo: '/catalogs/customer',
              },
              {
                key: 'newDrivers',
                value: data.week.newDrivers,
                linkTo: '/catalogs/drivers',
              },
              {
                key: 'towRequest',
                value: data.week.towRequest,
                // Counts OPEN requests created in the last 7 days. `isDashboard`
                // is deliberately absent: that branch ignores `status` and forces
                // `towProviderId == null`, which is the *other* tile below — the
                // two used to share one URL and land on the same list. Without the
                // flag the tow-request query applies `status` but no date bound, so
                // this is the closest honest target; an exact one needs a date
                // filter outside the dashboard branch (BACKEND_FOLLOWUPS).
                linkTo: '/tow-requests?status=OPEN',
              },
              // roadSideAssintence: no dedicated route for roadside-assistance sub-type yet
              { key: 'roadSideAssintence',           value: data.week.roadSideAssintence,           linkTo: undefined },
              {
                key: 'towRequestWhitoutTowProvider',
                value: data.week.towRequestWhitoutTowProvider,
                // `isDashboard` *is* the "without tow provider" filter — the branch
                // adds `towProviderId == null` and applies the date window. Dropped
                // the `status=OPEN` that used to ride along: the branch ignores it,
                // but TowRequestsList still rendered it as an active filter chip,
                // so the UI advertised a filter that was not being applied.
                linkTo: '/tow-requests?' + new URLSearchParams({ startDate: daysAgoIso(7), endDate: todayIso(), isDashboard: 'true' }).toString(),
              },
            ] satisfies Array<{ key: string; value: number; linkTo: string | undefined }>
          ).map(({ key, value, linkTo }) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={key}>
              <KpiCard
                label={t(`${ns}.lastWeek.${key}`)}
                value={value}
                icon={KPI_VISUALS[key]?.icon}
                tone={KPI_VISUALS[key]?.tone}
                linkTo={linkTo}
              />
            </Grid>
          ))
        }
      </Grid>

      {/* ------------------------------------------------------------------ */}
      {/* Live tow requests — the dispatch feed from mockup 03 screen 1.      */}
      {/* ------------------------------------------------------------------ */}
      <Box sx={{ mt: 3 }}>
        <LiveTowRequests />
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Section 3 — Two 7-day activity charts                              */}
      {/*                                                                    */}
      {/* Was one "Driver Activity" panel, which was heading for bloat and    */}
      {/* was mislabelled besides: the series has only ever counted tow       */}
      {/* requests. Split into the two jobs the business actually runs —      */}
      {/* tows and roadside — because they have different providers and       */}
      {/* different money, and one number covering both answers neither.     */}
      {/* ------------------------------------------------------------------ */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ActivityBarChart
            titleId="admin-home-tow-activity-title"
            title={t('ADMIN-HOME.driverActivity.title')}
            helpText={t('ADMIN-HOME.driverActivity.help')}
            helpAriaLabel={t('ADMIN-HOME.driverActivity.ariaLabel')}
            data={driverActivity}
            loading={loading || !data}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ActivityBarChart
            titleId="admin-home-roadside-activity-title"
            title={t('ADMIN-HOME.roadsideActivity.title')}
            helpText={t('ADMIN-HOME.roadsideActivity.help')}
            helpAriaLabel={t('ADMIN-HOME.roadsideActivity.ariaLabel')}
            data={roadsideActivity}
            loading={loading || !data}
          />
        </Grid>
      </Grid>

      {/* ------------------------------------------------------------------ */}
      {/* Section 4 — Quick lookup                                            */}
      {/* ------------------------------------------------------------------ */}
      <Paper
        sx={{ p: 2, mt: 3 }}
        role="region"
        aria-labelledby="admin-home-quick-lookup-title"
      >
        <Typography
          id="admin-home-quick-lookup-title"
          variant="subtitle1"
          fontWeight={600}
          sx={{ mb: 2 }}
        >
          {t('home.administrator.quickLookupTitle')}
        </Typography>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (claimNumber.trim()) {
              void navigate(`/claims?claimNumber=${encodeURIComponent(claimNumber.trim())}`);
            }
          }}
          sx={{ display: 'flex', gap: 1 }}
        >
          <TextField
            label={t('home.administrator.quickLookupLabel')}
            value={claimNumber}
            onChange={(e) => setClaimNumber(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button type="submit" variant="contained" aria-keyshortcuts="Enter" startIcon={<SearchIcon />}>
            {t('home.administrator.quickLookupButton')}
          </Button>
        </Box>
      </Paper>
      <BackToTopFab />
    </Box>
  );
}
